import { useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowRightIcon } from '@radix-ui/react-icons'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Box, Button, Flex, Grid, Heading, Separator, Text, TextField } from '@radix-ui/themes'
import { parseReading, readingValue } from '@willie/installation-guides'
import { Ops } from '@willie/schemas'
import { FieldShell } from '~/components/FieldShell'
import { ImageLightbox, openOnPlainClick } from '~/components/ImageLightbox'
import { formatDayWithYear, formatTimestamp } from '~/utils/date'
import { useTRPC } from '~/utils/trpc'
import { useOpsSignedUrl } from '~/features/installations/useOpsSignedUrl'
import {
  expectedIndex,
  formatM3,
  formatPercent,
  gapPercent,
  TOLERANCE_PERCENT,
  type CaptureDetail
} from '~/features/index-captures/alignment'
import { roundLabel } from '~/features/index-captures/labels'
import { AlignmentVerdict } from './AlignmentVerdict'
import { CaptureRounds } from './CaptureRounds'
import { DecisionForm } from './DecisionForm'
import { RecordReadingForm } from './RecordReadingForm'

interface CaptureDetailPanelProps {
  captureId: string
}

interface FieldProps {
  label: string
  action?: ReactNode
  hint?: string
  children: ReactNode
}

const Field = ({ label, action, hint, children }: FieldProps) => (
  <FieldShell
    label={label}
    action={action}
    hint={
      hint ? (
        <Text size="1" color="gray">
          {hint}
        </Text>
      ) : null
    }
  >
    <Text as="div" size="2">
      {children}
    </Text>
  </FieldShell>
)

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <Flex direction="column" gap="3">
    <Heading size="3">{title}</Heading>
    {children}
  </Flex>
)

const Photo = ({ detail }: { detail: CaptureDetail }) => {
  const [open, setOpen] = useState<number | null>(null)
  const { data: photoUrl, isPending } = useOpsSignedUrl(detail.photoPath)
  if (detail.photoPath && isPending) return null
  if (!photoUrl) {
    return (
      <Text size="2" color="gray">
        The photo no longer resolves in storage.
      </Text>
    )
  }
  return (
    <>
      <a href={photoUrl} target="_blank" rel="noreferrer" onClick={openOnPlainClick(() => setOpen(0))}>
        <img
          src={photoUrl}
          alt="The meter index as photographed"
          className="max-h-[28rem] w-full rounded-[var(--radius-3)] border border-[color:var(--gray-6)] object-contain"
        />
      </a>
      <ImageLightbox images={[photoUrl]} openIndex={open} onOpenChange={setOpen} />
    </>
  )
}

const IndexInput = ({ detail, onCorrected }: { detail: CaptureDetail; onCorrected: () => Promise<void> }) => {
  const trpc = useTRPC()
  const [draft, setDraft] = useState(detail.readingValue === null ? '' : String(detail.readingValue))
  const correct = useMutation(trpc.ops.indexCaptures.correctReading.mutationOptions({ onSuccess: onCorrected }))

  const parsed = parseReading(draft)
  const value = parsed.ok ? readingValue(parsed.digits) : null
  const changed = value !== null && value !== detail.readingValue
  const save = () => {
    if (changed && !correct.isPending) correct.mutate({ captureId: detail.id, readingValue: value })
  }

  return (
    <FieldShell
      label="Index (m³)"
      hint={
        correct.error ? (
          <Text size="1" color="red">
            {correct.error.message}
          </Text>
        ) : null
      }
    >
      <TextField.Root
        inputMode="decimal"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => event.key === 'Enter' && save()}
      >
        <TextField.Slot side="right">
          <Button size="1" variant="soft" disabled={!changed} loading={correct.isPending} onClick={save}>
            Save
          </Button>
        </TextField.Slot>
      </TextField.Root>
    </FieldShell>
  )
}

interface ReadingsProps {
  detail: CaptureDetail
  correctable: boolean
  onCorrected: () => Promise<void>
}

const Readings = ({ detail, correctable, onCorrected }: ReadingsProps) => (
  <Grid columns="2" gap="3">
    {correctable ? (
      <IndexInput detail={detail} onCorrected={onCorrected} />
    ) : (
      <Field label="Index">{formatM3(detail.readingValue)}</Field>
    )}
    {detail.comparedAt ? (
      <>
        <Field
          label="Index expected"
          hint={
            detail.anchor
              ? `${formatM3(detail.anchor.value)} on ${formatDayWithYear(detail.anchor.reading_at)} + telemetry`
              : undefined
          }
        >
          {formatM3(expectedIndex(detail))}
        </Field>
        <Field label="Consumption" hint={`Telemetry says ${formatM3(detail.telemetryConsumption)}`}>
          {formatM3(detail.indexConsumption)}
        </Field>
        <Field label="Gap" hint={`${formatM3(detail.delta)} · tolerance ${formatPercent(TOLERANCE_PERCENT)}`}>
          {formatPercent(gapPercent(detail))}
        </Field>
      </>
    ) : null}
    <Field label="Photo taken">{formatTimestamp(detail.capturedAt)}</Field>
    <Field label="Uploaded by">{detail.uploadedBy ?? '—'}</Field>
  </Grid>
)

const RunComparison = ({ detail, onCompared }: { detail: CaptureDetail; onCompared: () => Promise<void> }) => {
  const trpc = useTRPC()
  const compare = useMutation(trpc.ops.indexCaptures.compare.mutationOptions({ onSuccess: onCompared }))
  return (
    <Flex direction="column" gap="2" align="start">
      <Button loading={compare.isPending} onClick={() => compare.mutate({ captureId: detail.id })}>
        {detail.status === 'flagged' ? 'Compare again' : 'Run comparison now'}
      </Button>
      {compare.error ? (
        <Text size="2" color="red">
          {compare.error.message}
        </Text>
      ) : null}
    </Flex>
  )
}

const Resolution = ({ resolution }: { resolution: NonNullable<CaptureDetail['resolution']> }) => (
  <Grid columns="2" gap="3">
    <Field label="Decided by">{resolution.by ?? '—'}</Field>
    <Field label="On">{formatTimestamp(resolution.at)}</Field>
    {resolution.note ? (
      <Box className="col-span-2">
        <Field label="Note">{resolution.note}</Field>
      </Box>
    ) : null}
  </Grid>
)

const Context = ({ detail }: { detail: CaptureDetail }) => (
  <Grid columns={{ initial: '2', sm: '3' }} gap="3">
    <Field label="Installation">
      <Link to="/installations/$installationId" params={{ installationId: String(detail.installationId) }}>
        #{detail.installationId} {detail.name ?? ''}
      </Link>
    </Field>
    <Field label="Organization">{detail.orgName ?? '—'}</Field>
    <Field label="Site">{detail.siteName ?? '—'}</Field>
    <Field label="Station">{detail.stationName ?? '—'}</Field>
    <Field label="Profile">{detail.installationProfile ?? '—'}</Field>
    <Field label="Round">{roundLabel(detail.round)}</Field>
    <Field label="Requested">{formatTimestamp(detail.requestedAt)}</Field>
    <Field label="Email sent">{formatTimestamp(detail.sentAt)}</Field>
    <Field label="Reminders sent">{detail.reminderCount}</Field>
    <Field label="Compared">{formatTimestamp(detail.comparedAt)}</Field>
    <Field label="Window asked for">
      {formatTimestamp(detail.windowFrom)} → {formatTimestamp(detail.windowTo)}
    </Field>
    <Field label="Window measured">
      {detail.anchor
        ? `${formatTimestamp(detail.anchor.reading_at)} → ${formatTimestamp(detail.capturedAt)}`
        : '—'}
    </Field>
  </Grid>
)

const OpenInstallationButton = ({ installationId }: { installationId: number }) => (
  <Button asChild size="1" variant="soft">
    <Link to="/installations/$installationId" params={{ installationId: String(installationId) }}>
      Open installation #{installationId}
      <ArrowRightIcon />
    </Link>
  </Button>
)

const CaptureDetailBody = ({ detail, onChanged }: { detail: CaptureDetail; onChanged: () => Promise<void> }) => {
  const awaitingPhoto = detail.status === 'pending' || detail.status === 'sent'
  const awaitingVerdict = Ops.IndexCaptures.isAwaitingVerdict(detail.status)

  return (
    <Flex direction="column" gap="5">
      {awaitingPhoto ? (
        <Section title="Upload the photo">
          <RecordReadingForm detail={detail} onRecorded={onChanged} />
        </Section>
      ) : (
        <Grid columns={{ initial: '1', md: '2' }} gap="5">
          <Flex direction="column" gap="4" className="min-w-0">
            <AlignmentVerdict detail={detail} />
            <Readings detail={detail} correctable={awaitingVerdict} onCorrected={onChanged} />
            {awaitingVerdict ? <RunComparison detail={detail} onCompared={onChanged} /> : null}
          </Flex>
          <Photo detail={detail} />
        </Grid>
      )}

      {detail.status === 'flagged' ? (
        <Section title="Override">
          <DecisionForm detail={detail} onDecided={onChanged} />
        </Section>
      ) : null}

      {detail.resolution ? (
        <Section title="Decision">
          <Resolution resolution={detail.resolution} />
        </Section>
      ) : null}

      <Separator size="4" />
      <Context detail={detail} />
    </Flex>
  )
}

/** Opens on one capture; the rounds list switches it to the installation's others in place. */
export const CaptureDetailPanel = ({ captureId }: CaptureDetailPanelProps) => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [viewedId, setViewedId] = useState(captureId)
  const query = useQuery({
    ...trpc.ops.indexCaptures.get.queryOptions({ captureId: viewedId }),
    placeholderData: keepPreviousData
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: trpc.ops.indexCaptures.pathKey() })

  if (query.isLoading) return <Text color="gray">Loading…</Text>
  if (query.error) return <Text color="red">{query.error.message}</Text>
  if (!query.data) return null

  return (
    <Flex direction="column" gap="5">
      <Flex justify="end">
        <OpenInstallationButton installationId={query.data.installationId} />
      </Flex>
      <CaptureRounds rounds={query.data.rounds} currentCaptureId={viewedId} onSelect={setViewedId} />
      <CaptureDetailBody key={query.data.id} detail={query.data} onChanged={refresh} />
    </Flex>
  )
}
